/**
 * Speedies (F-129): React Query hooks over `api/speedies.ts`. Every query is disabled while the
 * `speedies` flag is off, so no Speedy request is made then (A3-ALIGN §2.9).
 *
 * Writes refresh what they affect through `invalidateSpeedyReads`, called on the query client
 * itself, so an unmounted component never skips it (A3-ALIGN Grok finding 17).
 */
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import { isSpeediesEnabled } from '@/lib/feature-flags';
import { useCurrentUser } from '@/api/hooks';
import {
  fetchMyRejection,
  fetchMySpeedies,
  fetchMySpeedyCardStats,
  fetchMySpeedyInsights,
  fetchSavedSpeedies,
  fetchSpeedyCards,
  fetchSpeedyDetails,
  fetchSpeedyFacets,
  fetchSpeedyFeed,
  fetchSpeedySeries,
  fetchSpeedyTagSuggestions,
  deleteSpeedy,
  oldestPublishedAt,
  searchSpeedies,
  setSpeedySaved,
  speedyKeys,
  submitSpeedyUpdate,
  speedyUploadAbort,
  withMyFlags,
  type SpeedySearchResponse,
  type SpeedyFeedCard,
  type SpeedyFilters,
  type SpeedySearchRequest,
} from '@/api/speedies';

const FEED_PAGE = 20;

/** Everything a Speedy write can change, refreshed together. */
export function invalidateSpeedyReads(
  qc: QueryClient,
  what: 'saved' | 'completion' | 'mine' | 'all' = 'all',
): void {
  const keys: (readonly unknown[])[] = what === 'mine'
    ? [['speedy', 'mine'], ['speedy', 'cards-by-id']]
    : what === 'saved'
    ? [['speedy', 'saved'], ['speedy', 'feed'], ['speedy', 'search'], ['speedy', 'cards-by-id'], ['speedy', 'series-cards']]
    : what === 'completion'
    ? [
      ['speedy', 'feed'], ['speedy', 'search'], ['speedy', 'series'], ['speedy', 'saved'],
      ['speedy', 'cards-by-id'], ['speedy', 'series-cards'],
    ]
    : [['speedy']];
  for (const queryKey of keys) void qc.invalidateQueries({ queryKey });
}

/** The feed carousels' tiles (two rows of up to 10): unfinished first, never refetched while the feed is mounted. */
export function useSpeedyFeedTiles() {
  return useQuery({
    queryKey: speedyKeys.feedTiles(),
    queryFn: () => fetchSpeedyFeed({ limit: 20, excludeFinished: true }),
    enabled: isSpeediesEnabled(),
    staleTime: Infinity,
    gcTime: 10 * 60_000,
  });
}

/** The reel's own list (the header button): pages of 20, the next page before the oldest seen. */
export function useSpeedyFeed(enabled = true) {
  return useInfiniteQuery({
    queryKey: speedyKeys.feed(),
    queryFn: ({ pageParam }) => fetchSpeedyFeed({ limit: FEED_PAGE, before: pageParam, excludeFinished: false }),
    initialPageParam: null as string | null,
    getNextPageParam: (last: SpeedyFeedCard[]) => (last.length < FEED_PAGE ? undefined : oldestPublishedAt(last)),
    enabled: enabled && isSpeediesEnabled(),
    staleTime: 60_000,
  });
}

export const SEARCH_PAGE = 30;
/** The server never pages past this offset. */
const SEARCH_MAX_OFFSET = 1000;

/** Never retried on a refusal (4xx, rate limit); once on anything else. */
function retryUnlessRefused(count: number, err: unknown): boolean {
  const status = (err as { status?: number | null } | null)?.status ?? null;
  return count < 1 && !(status !== null && status >= 400 && status < 500);
}

/** The next page's offset: the one asked for plus 30, while below `total` and the server's cap. */
export function nextSearchOffset(last: SpeedySearchResponse & { offset: number }): number | undefined {
  const next = last.offset + SEARCH_PAGE;
  return next < last.total && next <= SEARCH_MAX_OFFSET ? next : undefined;
}

/**
 * The browse grid: pages of 30 by offset. The next offset is the one asked for plus 30 (the server
 * drops cards the viewer can't see, so a page can be short) while it is below `total`.
 * `request` must not carry `offset` or `limit`.
 */
export function useSpeedySearch(request: Omit<SpeedySearchRequest, 'offset' | 'limit'>, enabled = true) {
  return useInfiniteQuery({
    queryKey: speedyKeys.search(request),
    queryFn: ({ pageParam, signal }) =>
      searchSpeedies({ ...request, limit: SEARCH_PAGE, offset: pageParam }, signal)
        .then((page) => ({ ...page, offset: pageParam })),
    initialPageParam: 0,
    getNextPageParam: nextSearchOffset,
    enabled: enabled && isSpeediesEnabled(),
    staleTime: 30_000,
    placeholderData: (prev) => prev,
    retry: retryUnlessRefused,
  });
}

export function useSpeedyFacets(filters: SpeedyFilters) {
  return useQuery({
    queryKey: speedyKeys.facets(filters),
    queryFn: () => fetchSpeedyFacets(filters),
    enabled: isSpeediesEnabled(),
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });
}

/**
 * Tags that start with what's typed (2+ characters). With `popularWhenEmpty`, nothing typed gives
 * the most used tags instead (the "tap to add" chips when making a Speedy).
 */
export function useSpeedyTagSuggestions(prefix: string, opts: { limit?: number; popularWhenEmpty?: boolean } = {}) {
  const trimmed = prefix.trim();
  const limit = opts.limit ?? 5;
  return useQuery({
    queryKey: [...speedyKeys.tags(trimmed), limit],
    queryFn: () => fetchSpeedyTagSuggestions(trimmed, limit),
    enabled: isSpeediesEnabled() && (trimmed.length >= 2 || (!!opts.popularWhenEmpty && trimmed.length === 0)),
    staleTime: 5 * 60_000,
  });
}

export function useSpeedySeries() {
  return useQuery({
    queryKey: speedyKeys.series(),
    queryFn: fetchSpeedySeries,
    enabled: isSpeediesEnabled(),
    staleTime: 60_000,
  });
}

export function useSpeedyDetails(speedyId: string | null, organizationId: string | null) {
  return useQuery({
    queryKey: speedyKeys.details(speedyId ?? '', organizationId),
    queryFn: () => fetchSpeedyDetails(speedyId as string, organizationId),
    enabled: isSpeediesEnabled() && !!speedyId,
    staleTime: 60_000,
  });
}

/** Cards by id, with the viewer's saved and finished flags. */
export function useSpeedyCards(ids: string[]) {
  const { data: user } = useCurrentUser();
  return useQuery({
    queryKey: speedyKeys.cards(ids),
    queryFn: async () => withMyFlags(user!.id, await fetchSpeedyCards(ids)),
    enabled: isSpeediesEnabled() && ids.length > 0 && !!user?.id,
    staleTime: 60_000,
  });
}

export function useSavedSpeedies() {
  const { data: user } = useCurrentUser();
  return useQuery({
    queryKey: speedyKeys.saved(user?.id ?? ''),
    queryFn: () => fetchSavedSpeedies(user!.id),
    enabled: isSpeediesEnabled() && !!user?.id,
    staleTime: 30_000,
  });
}

/** Views, saves and the last 24 hours for each of the member's own Speedies (the My Speedies cards). */
export function useMySpeedyCardStats() {
  const { data: user } = useCurrentUser();
  return useQuery({
    queryKey: speedyKeys.mineStats(user?.id ?? ''),
    queryFn: fetchMySpeedyCardStats,
    enabled: isSpeediesEnabled() && !!user?.id,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });
}

/** The insights panel for one of the member's own Speedies; nothing while `speedyId` is null. */
export function useMySpeedyInsights(speedyId: string | null) {
  return useQuery({
    queryKey: speedyKeys.insights(speedyId ?? ''),
    queryFn: () => fetchMySpeedyInsights(speedyId!),
    enabled: isSpeediesEnabled() && !!speedyId,
    staleTime: 60_000,
  });
}

/** The member's own Speedies; refetched on focus and every 15 s while any is still in flight. */
export function useMySpeedies() {
  const { data: user } = useCurrentUser();
  return useQuery({
    queryKey: speedyKeys.mine(user?.id ?? ''),
    queryFn: () => fetchMySpeedies(user!.id),
    enabled: isSpeediesEnabled() && !!user?.id,
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
    refetchInterval: (q) =>
      (q.state.data ?? []).some((s) => ['uploading', 'processing', 'reading', 'checking'].includes(s.status))
        ? 15_000
        : false,
  });
}

/**
 * Deletes the member's own Speedy; it can sit in any list, so everything is refreshed. One still
 * uploading is cancelled instead (the server refuses to delete it).
 */
export function useDeleteSpeedy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ speedyId, uploading }: { speedyId: string; uploading: boolean }) => {
      if (uploading) await speedyUploadAbort(speedyId);
      else await deleteSpeedy(speedyId);
    },
    onSettled: () => invalidateSpeedyReads(qc, 'all'),
  });
}

/** Sends a reply to a rejection; the Speedy moves to "With a reviewer". */
export function useSubmitSpeedyUpdate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ speedyId, text }: { speedyId: string; text: string }) => submitSpeedyUpdate(speedyId, text),
    onSettled: (_d, _e, { speedyId }) => {
      void qc.invalidateQueries({ queryKey: speedyKeys.rejection(speedyId) });
      invalidateSpeedyReads(qc, 'mine');
    },
  });
}

export function useMyRejection(speedyId: string | null) {
  return useQuery({
    queryKey: speedyKeys.rejection(speedyId ?? ''),
    queryFn: () => fetchMyRejection(speedyId as string),
    enabled: isSpeediesEnabled() && !!speedyId,
  });
}

/** Save or unsave, optimistic on every card list that carries `saved_by_me`; rolled back on error. */
export function useToggleSpeedySaved() {
  const qc = useQueryClient();
  const { data: user } = useCurrentUser();
  return useMutation({
    mutationFn: ({ speedyId, saved }: { speedyId: string; saved: boolean }) =>
      setSpeedySaved(user!.id, speedyId, saved),
    onMutate: async ({ speedyId, saved }) => {
      await qc.cancelQueries({ queryKey: ['speedy'] });
      const before = qc.getQueriesData({ queryKey: ['speedy'] });
      const patch = (c: unknown) =>
        c && typeof c === 'object' && (c as { id?: string }).id === speedyId && 'saved_by_me' in (c as object)
          ? { ...(c as object), saved_by_me: saved }
          : c;
      qc.setQueriesData({ queryKey: ['speedy'] }, (data: unknown) => {
        if (Array.isArray(data)) return data.map(patch);
        if (data && typeof data === 'object' && Array.isArray((data as { results?: unknown[] }).results)) {
          return { ...(data as object), results: (data as { results: unknown[] }).results.map(patch) };
        }
        if (data && typeof data === 'object' && Array.isArray((data as { pages?: unknown[] }).pages)) {
          return {
            ...(data as object),
            pages: (data as { pages: unknown[] }).pages.map((p) => (Array.isArray(p) ? p.map(patch) : p)),
          };
        }
        return data;
      });
      return { before };
    },
    onError: (_err, _vars, ctx) => {
      for (const [key, data] of ctx?.before ?? []) qc.setQueryData(key, data);
    },
    onSettled: () => invalidateSpeedyReads(qc, 'saved'),
  });
}
